import { apiClient } from './client'

/**
 * The Compliance module's regulatory extracts, as the reports area calls them.
 *
 * These two endpoints are gated on `reports.regulatory.generate` and `reports.bureau.extract` —
 * the codes live in the reports family because that is who runs them, while the endpoints live
 * under `/compliance` because that is who owns the figures. For a while the reports gap list
 * claimed neither endpoint existed; the pinned contract was stale, not the server.
 */

export type PrudentialSchedule = 'Capital' | 'Liquidity' | 'AssetQuality'

/** The extract formats. No PDF: both files exist to be loaded into something, not read. */
export type ExtractFormat = 'Csv' | 'Xlsx'

const EXTENSION: Record<ExtractFormat, string> = { Csv: 'csv', Xlsx: 'xlsx' }

/**
 * One SDI prudential return schedule as at a date — capital, liquidity, or the classified
 * portfolio. Filing remains a manual step through ORASS; this produces the figures, not the filing.
 */
export async function exportPrudentialReturn(query: {
  schedule: PrudentialSchedule
  asOf: string
  branchId?: string | null
  format: ExtractFormat
}): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.get<Blob>('/compliance/prudential/extract', {
    params: {
      schedule: query.schedule,
      asOf: query.asOf,
      branchId: query.branchId ?? undefined,
      format: query.format,
    },
    responseType: 'blob',
  })

  return {
    blob: response.data,
    filename: filenameFrom(response.headers, `prudential-${query.schedule.toLowerCase()}`, query.format),
  }
}

/**
 * The credit-bureau submission file, one row per loan cycle as at the date. The column layout is
 * the build team's, not yet validated against the contracted bureau's specification — assumptions
 * register item A9.
 */
export async function exportCreditBureauExtract(query: {
  asOf: string
  format: ExtractFormat
}): Promise<{ blob: Blob; filename: string }> {
  const response = await apiClient.get<Blob>('/compliance/credit-bureau/extract', {
    params: { asOf: query.asOf, format: query.format },
    responseType: 'blob',
  })

  return {
    blob: response.data,
    filename: filenameFrom(response.headers, 'credit-bureau-extract', query.format),
  }
}

/** The server names the file in `Content-Disposition`; a stem plus the extension is the fallback. */
function filenameFrom(headers: Record<string, unknown>, stem: string, format: ExtractFormat): string {
  const disposition = headers['content-disposition']

  if (typeof disposition === 'string') {
    const match = /filename\*?=(?:UTF-8'')?"?([^;"]+)/i.exec(disposition)
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1])
      } catch {
        return match[1]
      }
    }
  }

  return `${stem}.${EXTENSION[format]}`
}
